// tslint:disable:ordered-imports
import { Button as MuiButton } from '@material-ui/core';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { IS_SERVER } from '../../common/constants';
import { isCrawlerUserAgent } from '../../common/helpers/isCrawlerUserAgent';
import { MARKET } from '../../config/browser';

import { StyledLtpActions, StyledLtpContent, StyledLtpDialog } from './LtpConsentModal.styles';
import { getLtpBrowserLanguage, LtpDisplayLanguage } from './ltpBrowserLanguage';
import { hasLtpConsent, setLtpConsent } from './ltpConsentStorage';
import { pushLtpConsentRestoredToDataLayer, pushLtpConsentToDataLayer } from './ltpDataLayer';
import {
  LTP_URL_PRIVACY_EN,
  LTP_URL_PRIVACY_ES,
  LTP_URL_TERMS_EN,
  LTP_URL_TERMS_ES,
  LTP_URL_US_STATE_SUPPLEMENT_EN,
  LTP_URL_US_STATE_SUPPLEMENT_ES,
} from './ltpPolicyUrls';

const extLink = { rel: 'noopener noreferrer', target: '_blank' };

const ltpEnSetForth =
  'and agree to the collection and use of your information by cookies and similar technologies, as set forth in our';

const LtpBodyEn = () => (
  <p id='ltp-consent-body'>
    Welcome to WarnerBros.com. By clicking &quot;Agree&quot;, you have read and agree to the{' '}
    <a href={LTP_URL_TERMS_EN} {...extLink}>
      Terms of Use
    </a>{' '}
    {ltpEnSetForth}{' '}
    <a href={LTP_URL_PRIVACY_EN} {...extLink}>
      Privacy Policy
    </a>
    . Clicking &quot;Agree&quot; does not impact privacy choices you may have under state laws. Learn more in our{' '}
    <a href={LTP_URL_US_STATE_SUPPLEMENT_EN} {...extLink}>
      US State Supplement
    </a>
    .
  </p>
);

const LtpBodyEs = () => (
  <p id='ltp-consent-body'>
    Al hacer clic en &quot;Aceptar&quot;, usted confirma que ha leído y acepta nuestros{' '}
    <a href={LTP_URL_TERMS_ES} {...extLink}>
      Términos de Uso
    </a>
    , y que consiente la recopilación y el uso de su información mediante cookies y tecnologías similares, tal como se
    establece en nuestra{' '}
    <a href={LTP_URL_PRIVACY_ES} {...extLink}>
      Política de privacidad
    </a>
    . Hacer clic en &quot;Aceptar&quot; no afecta las opciones de privacidad que pueda tener según las leyes de cada
    estado. Para obtener más información, consulte nuestro{' '}
    <a href={LTP_URL_US_STATE_SUPPLEMENT_ES} {...extLink}>
      Suplemento para Estados Unidos
    </a>
    .
  </p>
);

const noop = () => undefined;

export const LtpConsentModal = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<LtpDisplayLanguage>('en');

  const refreshVisibility = useCallback(() => {
    if (IS_SERVER) {
      return;
    }
    if (MARKET !== 'US') {
      return;
    }
    if (typeof window === 'undefined' || isCrawlerUserAgent(window.navigator.userAgent)) {
      return;
    }
    if (hasLtpConsent()) {
      return;
    }
    setLang(getLtpBrowserLanguage());
    setOpen(true);
  }, []);

  useEffect(() => {
    refreshVisibility();
  }, [refreshVisibility]);

  useEffect(() => {
    if (IS_SERVER || MARKET !== 'US') {
      return;
    }
    if (typeof window === 'undefined' || isCrawlerUserAgent(window.navigator.userAgent)) {
      return;
    }
    if (!hasLtpConsent()) {
      return;
    }
    pushLtpConsentRestoredToDataLayer();
  }, []);

  const onAgree = useCallback(() => {
    if (MARKET !== 'US') {
      return;
    }
    setLtpConsent();
    pushLtpConsentToDataLayer();
    setOpen(false);
  }, []);

  if (IS_SERVER || MARKET !== 'US' || !open) {
    return null;
  }

  return (
    <StyledLtpDialog
      PaperProps={{ 'data-testid': 'ltp-consent-modal' } as object}
      aria-describedby='ltp-consent-body'
      aria-labelledby='ltp-consent-title'
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      onClose={noop}
      open={open}
    >
      <StyledLtpContent>
        <h2 id='ltp-consent-title'>{lang === 'es' ? 'Términos legales y privacidad' : 'Legal Terms and Privacy'}</h2>
        {lang === 'es' ? <LtpBodyEs /> : <LtpBodyEn />}
        <StyledLtpActions>
          <MuiButton color='primary' data-testid='ltp-consent-agree' onClick={onAgree} variant='contained'>
            {lang === 'es' ? 'Aceptar' : 'Agree'}
          </MuiButton>
        </StyledLtpActions>
      </StyledLtpContent>
    </StyledLtpDialog>
  );
};
